import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

export const CHANGE_BASE = '/quality/change-control';
export const TYPES = ['ECO', 'PCN', 'PCR', 'PDR', 'PCR-PDR'];
export const TYPE_LABELS = { ECO: 'ECO', PCN: 'PCN', PCR: 'PCR', PDR: 'PDR', 'PCR-PDR': 'PCR / PDR' };
export const DEPARTMENTS = ['Manager', 'Sales', 'Development', 'Production', 'Logistics'];
export const IMPACTS = [
  ['finishedGoods', 'Apply to finished goods'], ['soldGoods', 'Apply to sold goods'],
  ['zeroSeriesTest', '0-series / FAI testing required'], ['specification', 'Change specification'],
  ['mountingSpecification', 'Change mounting specification'], ['marineApproval', 'New marine approval required'],
  ['informCustomers', 'Inform customers'], ['customerAcceptance', 'Customer acceptance required'],
];
export const COSTS = [['disposal', 'Disposal'], ['development', 'Development'],
  ['productionTechnology', 'Production Technology'], ['test', 'Test'], ['other', 'Other']];
export const CONTENT_KEYS = ['title', 'recordDate', 'owner', 'description', 'reason', 'scope', 'affectedItems',
  'productFamily', 'productNumbers', 'suppliers', 'productionSites', 'impactChecks', 'soldGoodsNumber',
  'costImpacts', 'introduction', 'implementation', 'verification', 'notes', 'sections', 'templateRef',
  'applicability', 'sourceReferences', 'releaseBlockers', 'proposedDecision', 'requestKind', 'documentBlocks'];

export function canUseChangeControl(user) {
  const metadata = user?.publicMetadata || {};
  return [metadata.role, metadata.roles].flat().some(value => {
    if (typeof value !== 'string') return false;
    const role = value.trim().toLowerCase().replace(/[\s_]+/g, '-');
    return ['admin', 'service', 'quality-manager', 'production-engineer'].includes(role) || role.startsWith('service-');
  });
}

export function editContent(record = {}) {
  return Object.fromEntries(CONTENT_KEYS.filter(key => record[key] !== undefined)
    .map(key => [key, JSON.parse(JSON.stringify(record[key]))]));
}

export function newContent() {
  return { title: '', recordDate: new Date().toISOString().slice(0, 10), owner: '', description: '', reason: '', scope: '',
    affectedItems: [], productFamily: '', productNumbers: [], suppliers: [], productionSites: [],
    impactChecks: Object.fromEntries(IMPACTS.map(([key]) => [key, null])), costImpacts: { currency: 'EUR' },
    introduction: { mode: '', date: null, productionOrders: '', notes: '' }, implementation: '', verification: '',
    notes: '', sections: [], documentBlocks: [], applicability: '', sourceReferences: [], releaseBlockers: [], proposedDecision: '', requestKind: '' };
}

function useAuthenticatedQmsApi(base) {
  const { getToken } = useAuth();
  return useCallback(async (path = '', options = {}) => {
    const token = await getToken();
    if (!token) throw new Error('Sign in again to continue.');
    const multipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const response = await window.fetch(`${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}${base}${path}`, {
      ...options, headers: { Authorization: `Bearer ${token}`, ...(!multipart && options.body ? { 'Content-Type': 'application/json' } : {}), ...options.headers },
      ...(options.body ? { body: multipart ? options.body : JSON.stringify(options.body) } : {}),
    });
    let data;
    try { data = await response.json(); } catch { throw new Error(`The server returned an unreadable response (${response.status}). Your input has been kept.`); }
    if (!response.ok || data.ok === false) {
      const error = new Error(data.message || data.error || `Request failed (${response.status}).`);
      error.status = response.status; error.data = data;
      throw error;
    }
    return data;
  }, [getToken, base]);
}
export function useChangeApi() { return useAuthenticatedQmsApi('/quality/engineering-changes'); }
export function useChangeArchiveApi() { return useAuthenticatedQmsApi('/quality/archives'); }

// Retain one operation identity across an uncertain response; a changed payload gets a new identity.
export function retryPayload(cache, key, body) {
  const signature = JSON.stringify({ key, body });
  if (cache.current?.signature !== signature) cache.current = { signature, requestId: window.crypto.randomUUID() };
  return { ...body, requestId: cache.current.requestId };
}

export function dateLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export function changedContent(left, right) {
  return CONTENT_KEYS.filter(key => JSON.stringify(left?.[key] ?? null) !== JSON.stringify(right?.[key] ?? null))
    .map(key => ({ key, before: left?.[key], after: right?.[key] }));
}
